import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import { Button, useDataProvider } from "react-admin";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CsvFileUploadInput from "../../../components/CsvFileUploadInput";
import TemplateUploadStudentSubsidyButton from "./TemplateUploadStudentSubsidyButton";
import TableResultUploadStudentSubsidies from "./TableResultUploadStudentSubsidies";
import { delay } from "utils/tools";
import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";

interface IStudentSubsidyFromCsv {
  studentIdentifier: string;
  studentIdentifierType: string;
  studentName: string;
  schoolId: string;
  subsidyPercentage: string;
  periodStartDate: string;
  periodEndDate: string;
  remark?: string;
  status: "failed" | "success" | "pending";
  message: string;
}

const useStyles = makeStyles({
  actionColor: {
    color: "#FFF",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
    marginRight: "14px",
  },
});

const CreateStudentSubsidyByCsvButton = () => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [finished, setFinished] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<IStudentSubsidyFromCsv[]>([]);
  const [studentSubsidies, setStudentSubsidies] = useState<
    IStudentSubsidyFromCsv[]
  >([]);

  const provider = useDataProvider();
  const classes = useStyles();
  const navMode = useSelector((state: ReduxState) => state.navMode);

  const handleCloseConfirm = () => {
    setFinished(false);
    setResults([]);
    setStudentSubsidies([]);
    setOpenConfirm(false);
  };

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setFinished(false);
    setResults([]);
    setStudentSubsidies([]);
    setOpenDialog(false);
  };

  // Import a single record. This also updates the UI with success/failure status for each row.
  const handleImport = (
    selectedStudentSubsidy: IStudentSubsidyFromCsv,
    index: number,
  ) => {
    provider
      .create("studentSubsidies", {
        data: {
          periodStartDate: selectedStudentSubsidy.periodStartDate,
          periodEndDate: selectedStudentSubsidy.periodEndDate,
          subsidyPercentage: selectedStudentSubsidy.subsidyPercentage,
          studentIdentifier: selectedStudentSubsidy.studentIdentifier,
          studentIdentifierType: selectedStudentSubsidy.studentIdentifierType,
          schoolId: selectedStudentSubsidy.schoolId,
          remark: selectedStudentSubsidy.remark,
        },
      })
      .then(() => {
        selectedStudentSubsidy.status = "success";
        studentSubsidies[index].message = "Imported";
        setResults([...studentSubsidies]);
      })
      .catch((err: any) => {
        selectedStudentSubsidy.status = "failed";
        studentSubsidies[index].message = `Error, ${err.message}`;
        setResults([...studentSubsidies]);
      });
  };

  // On confirm import, loop through all "Loading..." records and upload them one by one
  const handleConfirm = async () => {
    setOpenConfirm(false);
    setProcessing(true);
    setResults([...studentSubsidies]);

    for (const selectedStudentSubsidy of studentSubsidies) {
      if (selectedStudentSubsidy.status.toLowerCase() === "pending") {
        // Check if the school ID in the uploaded CSV row match the currently selected school
        // If not match, set an error message and continue to the next record
        // This prevents cross-school data updates
        if (navMode.school.id !== Number(selectedStudentSubsidy.schoolId)) {
          selectedStudentSubsidy.status = "failed";
          selectedStudentSubsidy.message =
            "Error: School ID in file does not match the selected school";
          setResults([...studentSubsidies]);
          continue;
        }

        const index = studentSubsidies.indexOf(selectedStudentSubsidy);
        handleImport(selectedStudentSubsidy, index);
        await delay(2000); // Add delay between each import
      }
    }

    setFinished(true);
    setProcessing(false);
  };

  useEffect(() => {
    if (studentSubsidies.length > 0) {
      setOpenConfirm(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentSubsidies]);

  return (
    <Fragment>
      <Button label="Import Student Subsidy Setting" onClick={handleOpenDialog}>
        <CloudUploadIcon />
      </Button>

      {/*Upload file and result dialog*/}
      <Dialog
        open={openDialog}
        aria-labelledby="upload-dialog-title"
        aria-describedby="upload-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="upload-dialog-title">
          Import Student Subsidy Setting by CSV
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultUploadStudentSubsidies records={results} />
          ) : (
            <CsvFileUploadInput
              csvFileType="student_subsidy_setting"
              setValues={(values) => {
                setStudentSubsidies(values);
              }}
              showErrorForEmptyColumns
              isHaveNullableField
            />
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <TemplateUploadStudentSubsidyButton isDisabled={processing} />
          )}
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
            className={classes.cancelButton}
          />
        </DialogActions>
      </Dialog>

      {/*Confirmation dialog before proceeding with import*/}
      <Dialog
        open={openConfirm}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        className={classes.dialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          Import Student Subsidy Setting by CSV
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" color="initial">
            Are you sure you want to upload this student subsidy setting file?
            Uploading this file will{" "}
            <strong>replace the current subsidy setting</strong> for the
            students included in the file.
            <br />
            <br />
            <strong>Note:</strong> Please make sure the{" "}
            <strong>studentIdentifierType</strong> and{" "}
            <strong>studentIdentifier</strong> are correctly filled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirm} color="primary" label="Confirm" />
          <Button
            onClick={handleCloseConfirm}
            color="secondary"
            label="back"
            className={classes.cancelButton}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default CreateStudentSubsidyByCsvButton;
